/**
 * AchievementSheet (Plan 02-14, Task 2 / UI-SPEC §9 + §"<AchievementSheet>").
 *
 * BottomSheet wrapper that surfaces the unlocked detail view:
 *   - enlarged AchievementBadge at size=128
 *   - title (heading-m, extrabold) — from achievement.<key>.title
 *   - description (Norwegian Bokmål, ink-2) — from achievement.<key>.description
 *   - "Låst opp {{date}}" — formatted relative date (Norwegian)
 *
 * Family-rank line ("Du var #N i familien til å låse opp denne") is omitted
 * in v1 — the API does not yet expose ordering. The i18n key is reserved
 * (`achievements.sheet.familyRank`) for a follow-up plan that adds the
 * server-side rank computation.
 *
 * Plan 15 (L4 takeover) reuses AchievementBadge at size=240 directly; this
 * sheet is the "after-the-fact" detail surface for an already-unlocked entry.
 */
import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import { BottomSheet } from '../../components/primitives/BottomSheet';
import { AchievementBadge } from '../../components/primitives/AchievementBadge';
import type { AchievementView } from './useAchievementsQuery';

interface AchievementSheetProps {
  open: boolean;
  onClose: () => void;
  achievement: AchievementView | null;
}

function formatUnlockDate(iso: string | null, locale = 'nb-NO'): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return new Intl.DateTimeFormat(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    }).format(d);
  } catch {
    return '';
  }
}

export function AchievementSheet({
  open,
  onClose,
  achievement,
}: AchievementSheetProps): ReactNode {
  const { t } = useTranslation();

  if (!achievement) {
    return (
      <BottomSheet open={open} onClose={onClose}>
        <div data-testid="achievement-sheet" />
      </BottomSheet>
    );
  }

  const { key, title_key, secret, unlocked, unlocked_at } = achievement;
  const title = title_key ? t(title_key) : '';
  const descriptionKey = `achievement.${key}.description`;
  const description = t(descriptionKey);
  const dateLabel = formatUnlockDate(unlocked_at);

  return (
    <BottomSheet open={open} onClose={onClose}>
      <div
        data-testid="achievement-sheet"
        data-key={key}
        className="flex flex-col items-center gap-4 py-4"
      >
        <AchievementBadge
          keyName={key}
          unlocked={unlocked}
          secret={secret}
          size={128}
        />
        {title ? (
          <h2 className="m-0 text-center text-heading-m font-extrabold text-ink">
            {title}
          </h2>
        ) : null}
        {description ? (
          <p className="m-0 max-w-sm text-center text-body-s leading-relaxed text-ink-2">
            {description}
          </p>
        ) : null}
        {unlocked && dateLabel ? (
          <p className="m-0 text-caption font-bold text-accent-text">
            {t('achievements.sheet.unlockedAt', { date: dateLabel })}
          </p>
        ) : null}
      </div>
    </BottomSheet>
  );
}
